import { useEffect, useRef, useState } from 'react'
import {
  Box, ClickAwayListener, InputAdornment, InputLabel, TextField,
} from '@mui/material'
import { DateRange } from 'react-date-range'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import moment from 'moment'
import { arEG, enUS } from 'date-fns/locale'
import { ReactComponent as Calendar } from '../../images/calendar.svg'
import ArrowLeft from '../../images/arrowLeft.svg'
import { ReactComponent as TeamSnap } from '../../images/TeamSnap.svg'
import UseTranslation from '../../hooks/UseTranslation'

const OfoqDateRingPicker = ({
  name,
  refState,
  defaultOption,
  required,
  customBtn,
  customStyle,
  customPlaceHolder,
  info,
  error,
  onBlur,
  maxDate,
  minDate,
  ...rest
}) => {
  const splitName = name?.split('_').join(' ')
  const { t, language } = UseTranslation()
  const anchorRef = useRef(null)

  const initValue = [
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection',
    },
  ]
  const [selectedDates, setSelectedDates] = useState(initValue)
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  // 0 => not selected , 1 => select the start date. 2 => select the end date
  const isInit = useRef(0)

  const handleDateChange = (value) => {
    setSelectedDates([value.selection])
    isInit.current += 1
  }

  useEffect(() => {
    if (defaultOption) {
      setSelectedDates(defaultOption.length
        ? defaultOption
        : initValue)
    }
  }, [defaultOption])

  useEffect(() => {
    if (isPickerOpen && isInit.current === 2) {
      setIsPickerOpen(false);
    }
  }, [selectedDates]);

  const formattedValue = (!defaultOption?.length && isInit.current === 0) ? null
    : `${moment(selectedDates?.[0]?.startDate).format('DD MMM YYYY')} -
  ${moment(selectedDates?.[0]?.endDate).format('DD MMM YYYY')}`

  if (!isPickerOpen && isInit.current >= 1) {
    refState(selectedDates)
    isInit.current = 0
  }

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }

    setIsPickerOpen(false)
  }

  return (
    <Box
      sx={[
        styles.dateContainer,
        customStyle,
        styles.dateRing,
      ]}
      ref={anchorRef}
    >
      <Box sx={styles.labelContainer}>
        <InputLabel
          htmlFor={name}
          variant="heading6"
          sx={styles.formInputLabel}
        >
          {splitName}
          {required && <TeamSnap style={styles.snapIcon} data-testid="teamsnap-icon" />}
        </InputLabel>
        {info || ''}
      </Box>

      <Box
        sx={[styles.inputDataContainer, customBtn && styles.dateBtnContainer]}
        onClick={() => setIsPickerOpen(!isPickerOpen)}
      >
        <TextField
          value={formattedValue || ''}
          sx={{ marginTop: '10px', width: '100%', color: 'black' }}
          SelectProps={{
            onClose: () => setIsPickerOpen(false),
            onOpen: () => setIsPickerOpen(true),
          }}
          input
          inputProps={{
            placeholder: customPlaceHolder || `${t?.Select} ${splitName}`,
          }}
          InputProps={{
            endAdornment:
              <InputAdornment position="end" sx={{ cursor: 'pointer' }}><Calendar /></InputAdornment>,

          }}
          error={error}
          onBlur={onBlur}
          {...rest}

          disabled
        />
        {customBtn && <Box onClick={() => setIsPickerOpen(true)}>{customBtn}</Box>}
      </Box>
      {isPickerOpen &&
        <ClickAwayListener onClickAway={handleClose}>
          <DateRange
            onChange={(value) => handleDateChange(value)}
            ranges={selectedDates}
            color="#29CC8B"
            rangeColors={['#29CC8B']}
            editableDateInputs
            moveRangeOnFirstSelection={false}
            locale={language === 'AR' ? arEG : enUS}
            maxDate={maxDate}
            minDate={minDate}
          />
        </ClickAwayListener>

      }
    </Box>

  )
}

const styles = {
  dateContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    '& .MuiInputLabel-outlined.MuiInputLabel-marginDense': {
      transform: 'translate(14px,12px) scale(1)',
    },
    '& .MuiFormControl-marginDense': {
      marginTop: 20,
      marginBottom: 20,
      paddingRight: '0',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '15px',
      backgroundColor: 'background.default',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main !important',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: (theme) => `2px solid ${theme.palette.grey[200]}  !important`,
      transition: 'all 0.3s ease-in-out',
    },
    '& .MuiOutlinedInput-inputMarginDense': {
      padding: '12px',
    },
    '& .MuiAlert-root': {
      width: '100%',
      marginBottom: 16,
    },
    '& input': {
      fontSize: '14px',
      height: {
        xs: '4px',
        sm: '20px',
      },
    },
  },

  FormDisabled: {
    opacity: '0.5',
    pointerEvents: 'none',
  },
  labelContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  snapIcon: {
    marginInlineStart: '8px',
  },
  formInputLabel: {
    display: 'flex',
    alignItems: 'center',
    transform: 'translate(0,0) scale(1)',
    textTransform: 'capitalize',
    color: 'text.primary',
    fontSize: '14px',
  },
  inputDataContainer: {
    position: 'relative',
    display: 'flex',
    '& .Mui-disabled': {
      '-webkit-text-fill-color': 'black !important',
      fontSize: '14px',
    },
  },
  dateBtnContainer: {
    position: 'relative',
    '& .MuiFormControl-root, .MuiOutlinedInput-root, .MuiOutlinedInput-input': {
      position: 'absolute',
      visibility: 'hidden',
      height: '0',
      width: '0',
      padding: 0,
      margin: 0,
    },
  },
  dateRing: {
    '& .rdrCalendarWrapper': {
      direction: 'ltr /* @noflip */',
      width: '100%',
      maxWidth: 'fit-content',
      minWidth: '323px',
      margin: '0px auto',
      position: 'absolute',
      top: { xs: '75px', sm: '90px' },
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 9999,
    },
    '& .rdrDayToday .rdrDayNumber span:after': {
      backgroundColor: 'primary.main',
    },
    '& .rdrDateDisplayWrapper': {
      display: 'none',
    },
    '& .rdrMonthAndYearPickers select': {
      background: 'none',
      padding: '0',
      fontSize: '14px',
      fontWeight: '500',
      color: 'black',
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    '& .rdrMonthPicker': {
      margin: 0,
    },
    '& .rdrNextButton i': {
      border: 'none',
    },
    '& .rdrPprevButton i': {
      border: 'none',
    },
    '& .rdrNextPrevButton': {
      background: `url(${ArrowLeft}) no-repeat`,
      border: 'none',
      '&:hover': {
        backgroundColor: 'white',
        border: 'none',
        background: `url(${ArrowLeft}) no-repeat`,
      },

    },
    '& .rdrNextButton': {
      transform: 'rotate(180deg)',
    },
    '& .rdrMonth': {
      padding: 'inherit',
    },
  },
}

export default OfoqDateRingPicker
